const ONLY_ATTR = 'data-mcf-market-only';
const EXCEPT_ATTR = 'data-mcf-market-except';
const PENDING_ATTR = 'data-mcf-market-pending';
const PICK_KEY = 'verish-country-pick';
const AUTO_LOCALIZATION_KEY = 'marketAutoLocalized';

function normalizeCountry(country) {
  return String(country || '').trim().toUpperCase();
}

export function resolveMcfLocalization({
  shopifyCountry = '',
  detectedCountry = '',
  detectedCountrySupported = false,
  detectionComplete = false,
  autoLocalizationAttempted = false,
  honouredPick = '',
} = {}) {
  const activeCountry = normalizeCountry(shopifyCountry);
  const pickedCountry = normalizeCountry(honouredPick);

  if (pickedCountry && pickedCountry === activeCountry) {
    return { ready: Boolean(activeCountry), country: activeCountry };
  }

  if (!detectionComplete) return { ready: false, country: '' };

  const ipCountry = normalizeCountry(detectedCountry);
  if (
    ipCountry &&
    detectedCountrySupported &&
    ipCountry !== activeCountry &&
    !autoLocalizationAttempted
  ) {
    return { ready: false, country: '' };
  }

  return { ready: Boolean(activeCountry), country: activeCountry };
}

export function isMcfCountryAllowed(country, allowedCountries) {
  const activeCountry = normalizeCountry(country);
  if (!activeCountry) return false;

  return String(allowedCountries || '')
    .split(',')
    .some((code) => normalizeCountry(code) === activeCountry);
}

// The functions above are intentionally DOM-free so market fallback cases can
// be tested without Shopify. The adapter below only connects them to the theme.
if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const readSession = (key) => {
    try {
      return window.sessionStorage.getItem(key) || '';
    } catch (error) {
      return '';
    }
  };

  const shopifyCountry = () =>
    normalizeCountry(window.Shopify?.country || document.documentElement.dataset.shopifyCountry);

  const detectedCountry = () =>
    normalizeCountry(window.FoxTheme?.country || readSession('detectedCountry'));

  const detectionComplete = () =>
    window.FoxTheme?.countryDetectionComplete === true;

  const honouredPick = () => {
    const pickedCountry = normalizeCountry(readSession(PICK_KEY));
    return pickedCountry === shopifyCountry() ? pickedCountry : '';
  };

  const isSupportedCountry = (country) => {
    if (!country) return false;
    return Boolean(
      document.querySelector(
        `[data-disclosure-country] [data-country-code="${CSS.escape(country)}"]`
      )
    );
  };

  const elementsIn = (root, attribute) => {
    const selector = `[${attribute}]`;
    const elements = [];
    if (root?.matches?.(selector)) elements.push(root);
    if (root?.querySelectorAll) elements.push(...root.querySelectorAll(selector));
    return elements;
  };

  const apply = (root = document) => {
    const ipCountry = detectedCountry();
    const result = resolveMcfLocalization({
      shopifyCountry: shopifyCountry(),
      detectedCountry: ipCountry,
      detectedCountrySupported: isSupportedCountry(ipCountry),
      detectionComplete: detectionComplete(),
      autoLocalizationAttempted: Boolean(readSession(AUTO_LOCALIZATION_KEY)),
      honouredPick: honouredPick(),
    });

    if (!result.ready) return;

    elementsIn(root, ONLY_ATTR).forEach((element) => {
      if (isMcfCountryAllowed(result.country, element.getAttribute(ONLY_ATTR))) {
        element.removeAttribute(PENDING_ATTR);
      } else {
        element.remove();
      }
    });

    elementsIn(root, EXCEPT_ATTR).forEach((element) => {
      if (isMcfCountryAllowed(result.country, element.getAttribute(EXCEPT_ATTR))) {
        element.remove();
      } else {
        element.removeAttribute(PENDING_ATTR);
        element.removeAttribute('hidden');
      }
    });
  };

  apply();
  document.addEventListener('country:detected', () => apply());
  document.addEventListener('cart:updated', () => apply());
  document.addEventListener('collection:rerendered', () => apply());
  document.addEventListener('recommendations:loaded', () => apply(), true);
  // 퀵뷰는 드로어에 새로 주입된다 — 대기 속성을 여기서 풀어야 박스가 보인다
  document.addEventListener('quick-view:loaded', () => apply());
}
